import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import ExportModal from "../components/ExportModal";
import { SEND_COLUMNS, DEFAULT_KEYS } from "../config/exportColumns";
import { getExports, sendToCrm } from "../services/api";
import { Search, BarChart2, TrendingUp, TrendingDown, Building2, Landmark, Download, Upload, ClipboardList, Calendar, RefreshCw, Check, Undo2, Inbox } from 'lucide-react';

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";

const EXPORT_TYPES = [
  {Icon:BarChart2,  name:"Sales Lead List",                  desc:"High-priority companies and outreach-ready leads.",                      color:"#E8431A"},
  {Icon:TrendingUp, name:"Marketing Heatmap",                desc:"Country × Product Category heatmap and analysis.",                       color:"#22c55e"},
  {Icon:TrendingDown,name:"Growth Analysis",                 desc:"Competitor show, country and category growth reports.",                   color:"#960659"},
  {Icon:Building2,  name:"Company Master",                   desc:"Master list of companies with all intelligence and scores.",              color:"#F18F18"},
  {Icon:Search,     name:"Research Quality Queue",           desc:"Data quality issues and resolution status.",                             color:"#C1121F"},
  {Icon:Landmark,   name:"Competitor Show Year Comparison",  desc:"Year-on-year comparison for a selected competitor show.",                color:"#960659"},
  {Icon:Download,   name:"Selected Companies",               desc:"Export specific companies based on your selection.",                     color:"#5F1156"},
];

const RECENT_EXPORTS = [
  {name:"Sales Leads – Gulfood 2026",              type:"Sales Lead List",         typeBg:"#FCEBE5",typeC:"#E8431A",filters:"High Priority, Approved, ITE 2026",                  by:"Rahul Gupta",     date:"12 May 2026",records:148,size:"2.1 MB",status:"Complete"},
  {name:"Country Heatmap – WorldFood 2026",        type:"Marketing Heatmap",       typeBg:"#dcfce7",typeC:"#16a34a",filters:"All Regions, All Categories",                          by:"Priya Mehta",     date:"11 May 2026",records:72, size:"1.4 MB",status:"Complete"},
  {name:"Category Growth – Anuga 2026",            type:"Growth Analysis",         typeBg:"#EFDDE8",typeC:"#5F1156",filters:"2024 vs 2025 vs 2026",                                 by:"Rohit Singh",     date:"10 May 2026",records:94, size:"1.8 MB",status:"Complete"},
  {name:"Company Master – SIAL 2025",              type:"Company Master",          typeBg:"#fef4e6",typeC:"#9a5b00",filters:"All ITE Events, All Shows",                             by:"Rahul Gupta",     date:"09 May 2026",records:3218,size:"8.2 MB",status:"Complete"},
  {name:"Research Queue – May 2026",               type:"Research Quality Queue",  typeBg:"#fdecec",typeC:"#C1121F",filters:"High Severity, Unresolved",                            by:"Neha Verma",      date:"08 May 2026",records:286,size:"0.6 MB",status:"Complete"},
  {name:"Worldfood 2024 vs 2025 vs 2026",         type:"Competitor Show YoY",     typeBg:"#EBD0E0",typeC:"#5F1156",filters:"WorldFood Moscow 2024, 2025, 2026",                    by:"Arjun Nair",      date:"07 May 2026",records:211,size:"1.1 MB",status:"Complete"},
  {name:"Selected: AI / Digital Health companies",type:"Selected Companies",       typeBg:"#ccfbf1",typeC:"#0f766e",filters:"12 companies selected",                               by:"Priya Mehta",     date:"06 May 2026",records:12, size:"0.2 MB",status:"Complete"},
];

// Card display name → export_logs.export_type enum
const EXPORT_TYPE_KEY = {
  "Sales Lead List":                 "sales_lead_list",
  "Marketing Heatmap":               "marketing_heatmap",
  "Growth Analysis":                 "growth_analysis",
  "Company Master":                  "company_master",
  "Research Quality Queue":          "quality_queue",
  "Competitor Show Year Comparison": "show_yoy",
  "Selected Companies":              "selected_companies",
};

const STEPS = [
  {n:"1",title:"Filter & Select",desc:"Use the Lead Management module to filter, assign and approve companies for each ITE event."},
  {n:"2",title:"Review Queue",desc:"CRM-ready companies appear below. Review and confirm each record before sending."},
  {n:"3",title:"Send or Export",desc:"Click 'Send to CRM' for live integration or use Excel Export for offline distribution."},
  {n:"4",title:"Track Status",desc:"Sent records are marked in the Recent Exports table. Monitor delivery confirmation in the CRM."},
];

export default function SendPage() {
  const {
    selectedEvent, changeEvent, nav,
    currentRole, setCurrentRole, currentUser,
    notifs, setNotifs, notifOpen, setNotifOpen,
    companies, updateCo, showToast,
  } = useApp();

  const [sendingId,    setSendingId]    = useState(null);
  const [exportHistory, setExportHistory] = useState([]);
  const [exportCfg,    setExportCfg]    = useState(null);

  useEffect(() => {
    getExports()
      .then(data => { if (Array.isArray(data)) setExportHistory(data); })
      .catch(() => {});
  }, []);

  const approved = companies.filter(c => c.salesDecision === "Approved");
  const { events: allEvents } = useApp();
  const eventName = allEvents.find(e => e.ite_event_id === selectedEvent)?.name ?? selectedEvent ?? "—";

  function handleSend(co) {
    setSendingId(co.id);
    sendToCrm({ company_ids: [co.id], show_id: null, sent_by: currentRole })
      .catch(() => {})
      .finally(() => {
        updateCo(co.id, { sendStatus: "Sent" });
        showToast(`${co.name} sent to CRM`);
        setSendingId(null);
      });
  }

  function handleReturn(co) {
    updateCo(co.id, { salesDecision: null, sendStatus: null });
    showToast(`${co.name} returned to review queue`);
  }

  function openExport(typeName) {
    if (!approved.length) { showToast("No approved companies to export", "error"); return; }
    setExportCfg({
      title: `Export — ${typeName}`,
      columns: SEND_COLUMNS,
      defaultKeys: DEFAULT_KEYS.send,
      rows: approved,
      filenameBase: `${typeName.replace(/\s+/g, "_")}_${eventName}`,
      exportType: EXPORT_TYPE_KEY[typeName] || "company_master",
      scope: { ite_event_id: selectedEvent ?? null },
    });
  }

  const totalRecords = exportHistory.reduce((s,r)=>s+(r.records||0),0);

  return (
    <PageShell>
      <style>{`*::-webkit-scrollbar{width:5px}*::-webkit-scrollbar-thumb{background:#D8DADC;border-radius:3px}table.ct{width:100%;border-collapse:collapse;font-size:11px}table.ct th{text-align:left;padding:8px 10px;font-size:9px;font-weight:700;color:#818084;text-transform:uppercase;letter-spacing:0.4px;border-bottom:2px solid #D8DADC;background:#FAFAFA}table.ct td{padding:8px 10px;border-bottom:1px solid #F4F4F5}`}</style>

      <Sidebar active="send" onNav={nav}
        selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}/>

      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 28px",background:"#fff",borderBottom:"1px solid #D8DADC"}}>
          <div>
            <div style={{fontSize:20,fontWeight:800}}>Send & Export</div>
            <div style={{fontSize:11,color:"#818084",marginTop:2}}>Push CRM-ready leads and download Excel reports for {eventName}.</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <div style={{fontSize:11,color:"#818084"}}>
              <span style={{fontWeight:700,color:"#26262A"}}>{approved.filter(c=>c.sendStatus==="Sent").length}</span> sent •{" "}
              <span style={{fontWeight:700,color:"#F18F18"}}>{approved.filter(c=>!c.sendStatus).length}</span> pending
            </div>
          </div>
        </div>

        <div style={{flex:1,overflow:"auto",padding:"20px 28px 40px"}}>

          {/* How It Works */}
          <div style={{background:"#FCEBE5",border:"1px solid #F7C9B8",borderRadius:12,padding:"16px 20px",marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:700,color:"#E8431A",marginBottom:12}}>How Exports Work</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16}}>
              {STEPS.map(s => (
                <div key={s.n} style={{display:"flex",gap:10,alignItems:"start"}}>
                  <div style={{width:22,height:22,borderRadius:"50%",background:"#E8431A",color:"#fff",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>{s.n}</div>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:"#E8431A",marginBottom:2}}>{s.title}</div>
                    <div style={{fontSize:10,color:"#3F3F46",lineHeight:1.5}}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CRM Send Queue */}
          {approved.length > 0 && (
            <div style={{marginBottom:24}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <span style={{fontSize:14,fontWeight:700}}>CRM Send Queue</span>
                <span style={{padding:"2px 8px",borderRadius:12,background:"#E8431A",color:"#fff",fontSize:10,fontWeight:700}}>{approved.filter(c=>!c.sendStatus).length} pending</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {approved.map(co => (
                  <div key={co.id} style={{background:"#fff",borderRadius:10,border:"1px solid #D8DADC",padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:8}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700}}>{co.name}</div>
                        <div style={{fontSize:10,color:"#818084",marginTop:1}}>{co.country} · Fit Score {co.fitScore}</div>
                      </div>
                      {co.sendStatus === "Sent"
                        ? <span style={{padding:"3px 8px",borderRadius:5,background:"#dcfce7",color:"#16a34a",fontSize:9,fontWeight:700,display:"flex",alignItems:"center",gap:3}}><Check size={10} /> Sent</span>
                        : <span style={{padding:"3px 8px",borderRadius:5,background:"#fef4e6",color:"#9a5b00",fontSize:9,fontWeight:700}}>Pending</span>
                      }
                    </div>
                    <div style={{fontSize:10,color:"#3F3F46",marginBottom:10}}>
                      Priority: <strong>{co.priority}</strong> · Decision: <strong>{co.salesDecision}</strong>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      {co.sendStatus !== "Sent" ? (
                        <button
                          onClick={()=>handleSend(co)}
                          disabled={sendingId===co.id}
                          style={{flex:1,padding:"6px",borderRadius:6,border:"none",background:"#E8431A",color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F,opacity:sendingId===co.id?0.7:1,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                          <Upload size={12} />{sendingId===co.id?"Sending…":"Send to CRM"}
                        </button>
                      ) : (
                        <button disabled style={{flex:1,padding:"6px",borderRadius:6,border:"none",background:"#F4F4F5",color:"#818084",fontSize:10,fontWeight:700,cursor:"not-allowed",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                          <Check size={12} /> Sent
                        </button>
                      )}
                      <button
                        onClick={()=>handleReturn(co)}
                        style={{padding:"6px 10px",borderRadius:6,border:"1px solid #D8DADC",background:"#fff",fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F,color:"#3F3F46",display:"flex",alignItems:"center",gap:4}}>
                        <Undo2 size={12} /> Return
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {approved.length === 0 && (
            <div style={{background:"#fff",borderRadius:12,border:"1px dashed #D8DADC",padding:"32px",textAlign:"center",marginBottom:24}}>
              <div style={{display:"flex",justifyContent:"center",marginBottom:8}}><Inbox size={32} color="#818084" /></div>
              <div style={{fontSize:14,fontWeight:700,color:"#3F3F46",marginBottom:4}}>No companies in the CRM send queue</div>
              <div style={{fontSize:11,color:"#818084",marginBottom:16}}>Approve companies in Lead Management to add them to the queue.</div>
              <button onClick={()=>nav("leads")} style={{padding:"8px 18px",borderRadius:7,border:"none",background:"#E8431A",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                Go to Lead Management
              </button>
            </div>
          )}

          {/* Excel Export Types */}
          <div style={{marginBottom:24}}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>Excel Export Types</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:8}}>
              {EXPORT_TYPES.map(et => (
                <div key={et.name} style={{background:"#fff",borderRadius:10,border:"1px solid #D8DADC",padding:"14px 12px",display:"flex",flexDirection:"column",alignItems:"start",gap:6}}>
                  <div style={{marginBottom:4}}>{et.Icon && <et.Icon size={22} color={et.color} />}</div>
                  <div style={{fontSize:11,fontWeight:700,lineHeight:1.3}}>{et.name}</div>
                  <div style={{fontSize:9,color:"#818084",lineHeight:1.4,flex:1}}>{et.desc}</div>
                  <button onClick={()=>openExport(et.name)}
                    style={{width:"100%",padding:"6px",borderRadius:6,border:"none",background:et.color,color:"#fff",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:F,marginTop:4,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                    <Download size={10} /> Configure Export
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Export History Summary */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
            {[
              {label:"Total Exports",      value:exportHistory.length,                                          Icon:Upload,       c:"#E8431A",bg:"#FCEBE5"},
              {label:"Records Exported",   value:totalRecords.toLocaleString(),                                 Icon:ClipboardList,c:"#16a34a",bg:"#dcfce7"},
              {label:"This Month",         value:exportHistory.length,                                          Icon:Calendar,     c:"#5F1156",bg:"#EFDDE8"},
              {label:"Unique Types Used",  value:[...new Set(exportHistory.map(r=>r.type))].length,             Icon:BarChart2,    c:"#9a5b00",bg:"#fef4e6"},
            ].map(k => (
              <div key={k.label} style={{background:"#fff",borderRadius:10,border:"1px solid #D8DADC",padding:"14px 16px",display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:38,height:38,borderRadius:8,background:k.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {k.Icon && <k.Icon size={22} color={k.c} />}
                </div>
                <div>
                  <div style={{fontSize:18,fontWeight:800,fontFamily:M,color:k.c}}>{k.value}</div>
                  <div style={{fontSize:10,color:"#818084"}}>{k.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Exports */}
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <span style={{fontSize:14,fontWeight:700}}>Recent Exports</span>
              <div style={{display:"flex",gap:6}}>
                <input placeholder="Search exports…"
                  style={{padding:"6px 12px",borderRadius:6,border:"1px solid #D8DADC",fontSize:10,fontFamily:F,outline:"none",width:180}}/>
                <select style={{padding:"6px 8px",borderRadius:6,border:"1px solid #D8DADC",fontSize:10,fontFamily:F,color:"#818084"}}>
                  <option>Type: All</option>
                  {EXPORT_TYPES.map(e=><option key={e.name}>{e.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{background:"#fff",borderRadius:12,border:"1px solid #D8DADC",overflow:"hidden"}}>
              <table className="ct">
                <thead>
                  <tr>
                    <th>Export Name</th><th>Type</th><th>Filters Applied</th>
                    <th>Created By</th><th>Date</th><th>Records</th><th>File Size</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exportHistory.map((r,i) => (
                    <tr key={i}>
                      <td style={{fontWeight:600,fontSize:11}}>{r.name}</td>
                      <td><span style={{padding:"2px 7px",borderRadius:4,background:r.typeBg,color:r.typeC,fontSize:9,fontWeight:600}}>{r.type}</span></td>
                      <td style={{color:"#818084",maxWidth:180}}>{r.filters}</td>
                      <td style={{fontSize:10}}>{r.by}</td>
                      <td style={{fontSize:10,color:"#818084"}}>{r.date}</td>
                      <td style={{fontFamily:M,fontSize:10,fontWeight:600}}>{r.records.toLocaleString()}</td>
                      <td style={{fontFamily:M,fontSize:10}}>{r.size}</td>
                      <td><span style={{padding:"2px 8px",borderRadius:4,background:"#dcfce7",color:"#16a34a",fontSize:9,fontWeight:700,display:"inline-flex",alignItems:"center",gap:3}}><Check size={10} /> {r.status}</span></td>
                      <td>
                        <div style={{display:"flex",gap:6}}>
                          <button style={{padding:"4px 8px",borderRadius:5,border:"1px solid #D8DADC",background:"#fff",fontSize:9,fontWeight:600,cursor:"pointer",fontFamily:F,color:"#3F3F46",display:"flex",alignItems:"center",gap:3}}><Download size={10} /> Download</button>
                          <button style={{padding:"4px 8px",borderRadius:5,border:"1px solid #D8DADC",background:"#fff",fontSize:9,fontWeight:600,cursor:"pointer",fontFamily:F,color:"#3F3F46",display:"flex",alignItems:"center",gap:3}}><RefreshCw size={10} /> Re-run</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      <ExportModal
        config={exportCfg}
        createdBy={currentUser || currentRole}
        onClose={() => setExportCfg(null)}
        onExported={() => showToast("Export complete")}
      />
    </PageShell>
  );
}
